import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import BSCircularProgress from '../../ui/progress/circularProgress';
import GameByStatusCardPublicBasic from '../../ui/gameStatusCards/gameByStatusCardPublicBasic';
import { useColorMode } from '../../../hooks/colorModeProvider';

const toMillis = (value) => {
  if (!value) return 0;

  if (typeof value === 'number') return value;

  if (typeof value?.toDate === 'function') {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }

  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  if (typeof value?._seconds === 'number') return value._seconds * 1000;

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const EmbeddedPlayerGames = ({ pageMode = 'events' }) => {
  const { bsaNumber } = useParams();
  const navigate = useNavigate();
  const { mode, setMode } = useColorMode();
  const [loading, setLoading] = useState(true);
  const [gamesByEvent, setGamesByEvent] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    setMode('light');
  }, [mode, setMode]);

  const fetchGames = () => {
    if (!bsaNumber) return;

    setLoading(true);
    setError(null);

    fetch(`/api/game/player/${encodeURIComponent(bsaNumber)}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch games');
        return res.json();
      })
      .then((games) => {
        const gamesArray = Array.isArray(games) ? games : [];

        // This page shows one type only: event games on /public/mygames,
        // standalone quick games on /public/quickgames.
        const filtered =
          pageMode === 'quick'
            ? gamesArray.filter((game) => !game.event)
            : gamesArray.filter((game) => game.event);

        const grouped = filtered.reduce((acc, game) => {
          const key = pageMode === 'quick' ? 'Quick Games' : game.eventName || 'Unknown Event';
          if (!acc[key]) acc[key] = [];
          acc[key].push(game);
          return acc;
        }, {});

        // Newest quick games first.
        if (pageMode === 'quick') {
          Object.keys(grouped).forEach((key) => {
            grouped[key].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
          });
        }

        setGamesByEvent(Object.entries(grouped));
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchGames();
    // Refresh the list when the user comes back to the tab (game may have
    // been started/scored in another tab or from the /create flow).
    window.addEventListener('focus', fetchGames);
    return () => window.removeEventListener('focus', fetchGames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bsaNumber]);

  const handleDeleteGame = (game) => {
    if (!window.confirm(`Delete the game "${game.name}"? This cannot be undone.`)) return;

    fetch(`/api/game/public/${encodeURIComponent(game.id)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bsaNumber })
    })
      .then((res) => {
        if (!res.ok) {
          return res
            .json()
            .catch(() => ({}))
            .then((body) => {
              throw new Error(body.message || 'Failed to delete game');
            });
        }
        return null;
      })
      .then(() => fetchGames())
      .catch((err) => window.alert(err.message));
  };

  if (loading) {
    return (
      <Box p={3}>
        <BSCircularProgress caption={'Loading Games'} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack gap={3}>
        <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'}>
          {pageMode === 'quick' && (
            <Button variant="contained" color="secondary" onClick={() => navigate('/create')}>
              Create Quick Game
            </Button>
          )}
          <Button variant="outlined" color="info" onClick={fetchGames}>
            Refresh
          </Button>
        </Box>

        {gamesByEvent.length === 0 ? (
          <Box>
            <Typography variant="caption">You have no upcoming games scheduled.</Typography>
          </Box>
        ) : (
          gamesByEvent.map(([eventName, games]) => (
            <Stack key={eventName} gap={2}>
              <Typography variant="h6">{eventName}</Typography>
              <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
                {games.map((game) => (
                  <GameByStatusCardPublicBasic
                    key={game.id}
                    game={game}
                    actions={false}
                    showSectionWinners
                    bsaNumber={bsaNumber}
                    onDelete={!game.event ? () => handleDeleteGame(game) : undefined}
                  />
                ))}
              </Box>
            </Stack>
          ))
        )}
      </Stack>
    </Box>
  );
};

export default EmbeddedPlayerGames;
